import { merge, Subject, fromEvent, of } from 'rxjs';
import { takeUntil, first, switchMap } from 'rxjs/operators';

import {
  Component,
  OnDestroy,
  ChangeDetectorRef,
  AfterViewInit,
  ElementRef,
  OnInit,
  Input,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';

import { SnUser } from '@lib/api/entities';
import { SnCurrentUser } from '@lib/auth';

import { SnChannel } from '../../models';
import { SnChannelsService, SnChatService, SnMembersService } from '../../services';

@Component({
  selector: 'sn-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.scss'],
})
export class ChatWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public data: any;

  public channels: SnChannel[];
  public user: SnUser;
  public form = new UntypedFormGroup({
    channel_id: new UntypedFormControl(''),
  });
  public channel: SnChannel;
  public chatLoaded: boolean = false;
  public userHasAccess: boolean = false;
  private _destroy$ = new Subject();
  private _channelChanged$ = new Subject();

  constructor(
    public readonly currentUser: SnCurrentUser,
    private readonly _chatService: SnChatService,
    private readonly _channelsService: SnChannelsService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _elementRef: ElementRef,
    private readonly _membersService: SnMembersService,
  ) {
    this._chatService.chatIsReady$.pipe(takeUntil(this._destroy$)).subscribe((loaded) => {
      this.chatLoaded = loaded;
    });
    this.user = this.currentUser;
    merge(this._chatService.channelsLoaded$, this._chatService.channelsUpdated$)
      .pipe(first(), takeUntil(this._destroy$))
      .subscribe(() => {
        this.channels = this._chatService.channels;
      });
    if (this._chatService.channels) {
      this.channels = this._chatService.channels;
    }
  }

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    if (this.data) {
      const usersIds: string[] = this.data.map((user) => user.id);
      const payload = {
        users: usersIds.filter((id) => this.user.id !== id),
      };
      this._createChannel(payload);
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public selectChannel(channel: SnChannel): void {
    this.channel = channel;
    this.userHasAccess = this.channel.members.findIndex((user) => user.id === this.user.id) > -1;
    this._channelChanged$.next();
    this._initEvent();
    this._changeDetectorRef.detectChanges();
    setTimeout(() => {
      this._scrollToBottom();
    }, 1000);
    this._changeDetectorRef.detectChanges();
  }

  public unselectChannel(): void {
    this.channel = null;
    this._changeDetectorRef.detectChanges();
  }

  public join(): void {
    this._membersService
      .joinToChannel(this.channel.sid, this.channel.type)
      .pipe(
        switchMap((c) => {
          return this._chatService.getTwilioChannel(c.sid).pipe(
            switchMap((tc) => {
              c.twilioChannel = tc;
              return of(c);
            }),
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((channel) => {
        this.channel.users = channel.users;
        this.channel.twilioChannel = channel.twilioChannel;
        this.selectChannel(this.channel);
      });
  }

  public popout(): void {
    if (this.channel.twilioChannel) {
      window.open(`/chat/${this.channel.twilioChannel.sid}`);
    }
  }
  private _initEvent(): void {
    fromEvent(this.channel.twilioChannel, 'updated')
      .pipe(takeUntil(this._channelChanged$), takeUntil(this._destroy$))
      .subscribe(() => {
        this._scrollToBottom();
      });
  }

  private _scrollToBottom(): void {
    const div = this._elementRef.nativeElement.querySelector('.mat-card-content');
    div.scrollTop = div.scrollHeight;
  }

  private _createChannel(channelData: Object): void {
    this._channelsService
      .createDirectChannel(channelData)
      .pipe(
        switchMap((c) => {
          return this._chatService.getTwilioChannel(c.sid).pipe(
            switchMap((tc) => {
              c.twilioChannel = tc;
              this._chatService.setActiveChannel(c.sid);
              return of(c);
            }),
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((channel) => {
        this.selectChannel(channel);
        this._changeDetectorRef.detectChanges();
      });
  }
}
